<!-- AI.vue -->
<template>
  <Admin style="background: #f2f2f2">
    <v-main>
      <v-container fluid>
        <v-snackbar
          v-model="snackbar.show"
          :color="snackbar.color"
          :timeout="3000"
          top
        >
          {{ snackbar.message }}
          <template v-slot:action="{ attrs }">
            <v-btn text v-bind="attrs" @click="snackbar.show = false"
              >ปิด</v-btn
            >
          </template>
        </v-snackbar>

        <v-card class="rounded-xl tw-min-h-80">
          <v-card-text>
            <v-breadcrumbs :items="items">
              <template v-slot:divider>
                <v-icon>mdi-chevron-right</v-icon>
              </template>
              <template v-slot:item="{ item }">
                <v-breadcrumbs-item
                  :to="item.to"
                  :disabled="item.disabled"
                  :exact="true"
                >
                  {{ item.text }}
                </v-breadcrumbs-item>
              </template>
            </v-breadcrumbs>

            <!-- Prompts -->
            <div class="tw-mx-5">
              <div class="tw-flex tw-justify-between tw-items-center">
                <span
                  class="tw-font-bold tw-text-lg tw-text-black tw-tracking-wide"
                  >จัดการคำสั่ง Prompt</span
                >
                <div class="tw-flex tw-gap-2">
                  <v-btn outlined color="primary" :disabled="savingChanges" @click="openNewPromptDialog">
                    <v-icon left>mdi-plus</v-icon>
                    เพิ่มคำสั่ง
                  </v-btn>
                  <v-btn
                    color="success"
                    :loading="savingChanges"
                    :disabled="!hasUnsavedChanges || savingChanges"
                    @click="savePromptChanges"
                  >
                    <v-icon left>mdi-content-save</v-icon>
                    บันทึก
                  </v-btn>
                </div>
              </div>
            </div>
            
            <div v-if="!isInitializing">
              <!-- Prompt Type Filter -->
              <v-card-text class="tw-pb-0 tw-px-8">
                <v-chip-group
                  v-model="selectedPromptTypeFilter"
                  mandatory
                  @change="filterPromptsByType"
                >
                  <v-chip
                    filter
                    outlined
                    value="all"
                    class="tw-font-medium"
                    :color="selectedPromptTypeFilter === 'all' ? 'primary' : ''"
                  >
                    ทั้งหมด
                  </v-chip>
                  <v-chip
                    filter
                    outlined
                    :value="1"
                    class="tw-font-medium"
                    :color="selectedPromptTypeFilter === '1' ? 'indigo' : ''"
                  >
                    <v-icon left small>mdi-file-document-outline</v-icon>
                    Invoice
                  </v-chip>
                  <v-chip
                    filter
                    outlined
                    :value="2"
                    class="tw-font-medium"
                    :color="selectedPromptTypeFilter === '2' ? 'black' : ''"
                  >
                    <v-icon left small>mdi-credit-card-outline</v-icon>
                    Credit Advice
                  </v-chip>
                </v-chip-group>
              </v-card-text>

              <v-tabs v-model="currentTabIndex" class="tw-mt-2">
                <v-row class="tw-px-9">
                  <v-tab 
                    v-for="prompt in filteredPrompts" 
                    :key="prompt.promptId"
                    :class="[getPromptTypeClass(prompt.promptType), prompt.isActive ? 'active-prompt' : '']"
                  >
                    <v-icon small left :color="getPromptTypeColor(prompt.promptType)">
                      {{ getPromptTypeIcon(prompt.promptType) }}
                    </v-icon>
                    <span class="tw-relative">
                      {{ prompt.promptName }}
                      <v-badge
                        v-if="prompt.isActive"
                        color="success"
                        dot
                        left
                        class="active-badge"
                      ></v-badge>
                    </span>
                    <v-menu offset-y>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn icon small v-bind="attrs" v-on="on" @click.stop>
                          <v-icon small>mdi-dots-vertical</v-icon>
                        </v-btn>
                      </template>
                      <v-list dense>
                        <v-list-item 
                          v-if="!prompt.isActive"
                          @click="setActivePrompt(prompt)"
                          :disabled="isActivatingPrompt"
                        >
                          <v-list-item-title class="tw-flex tw-items-center">
                            <v-icon left small color="success">mdi-check-circle</v-icon>
                            ตั้งค่าเป็นใช้งาน
                          </v-list-item-title>
                        </v-list-item>
                        <v-list-item @click="openEditTypeDialog(prompt)">
                          <v-list-item-title class="tw-flex tw-items-center">
                            <v-icon left small color="primary">mdi-pencil</v-icon>
                            แก้ไขประเภท
                          </v-list-item-title>
                        </v-list-item>
                        <v-list-item @click="openDeleteDialog(prompt)">
                          <v-list-item-title class="tw-flex tw-items-center tw-text-error">
                            <v-icon left small color="error">mdi-delete</v-icon>
                            ลบคำสั่ง
                          </v-list-item-title>
                        </v-list-item>
                      </v-list>
                    </v-menu>
                  </v-tab>
                </v-row>

                <v-tab-item 
                  v-for="prompt in filteredPrompts" 
                  :key="prompt.promptId"
                >
                  <v-card flat class="tw-pt-4 tw-px-2">
                    <v-card-text>
                      <v-row>
                        <v-col cols="12" md="6" lg="3">
                          <v-select
                            :value="prompt.promptType || ''"
                            @input="(value) => updatePromptType(prompt.promptId, value)"
                            :items="promptTypes"
                            item-text="text"
                            item-value="value"
                            label="ประเภทคำสั่ง"
                            outlined
                            dense
                            return-object
                            class="tw-mb-4"
                          >
                            <template v-slot:selection="{ item }">
                              <div class="tw-flex tw-items-center">
                                <v-icon left :color="item.color">{{ item.icon }}</v-icon>
                                {{ item.text }}
                              </div>
                            </template>
                            <template v-slot:item="{ item }">
                              <div class="tw-flex tw-items-center">
                                <v-icon left :color="item.color">{{ item.icon }}</v-icon>
                                {{ item.text }}
                              </div>
                            </template>
                          </v-select>
                        </v-col>
                        <v-col cols="12" md="6" lg="3">
                          <v-card
                            class="tw-mb-4 tw-px-3 tw-py-2"
                            :color="prompt.isActive ? 'success lighten-5' : 'grey lighten-4'"
                            flat
                          >
                            <div class="tw-flex tw-justify-between tw-items-center">
                              <div>
                                <div class="tw-flex tw-items-center tw-mt-1">
                                  <v-icon small :color="prompt.isActive ? 'success' : 'grey'">
                                    {{ prompt.isActive ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                                  </v-icon>
                                  <span class="tw-ml-1 tw-text-sm">
                                    {{ prompt.isActive ? 'กำลังใช้งาน' : 'ไม่ใช้งาน' }}
                                  </span>
                                </div>
                              </div>
                              <v-btn
                                v-if="!prompt.isActive"
                                small
                                color="success"
                                outlined
                                :loading="isActivatingPrompt && activatingPromptId === prompt.promptId"
                                @click="setActivePrompt(prompt)"
                              >
                                เปิดใช้งาน
                              </v-btn>
                            </div>
                          </v-card>
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col cols="12">
                          <v-textarea
                            :value="prompt.promptText"
                            @input="(value) => updatePromptText(prompt.promptId, value)"
                            label="Prompt Text"
                            rows="35"
                            outlined
                            dense
                            persistent-placeholder
                          ></v-textarea>
                        </v-col>
                      </v-row>
                    </v-card-text>
                  </v-card>
                </v-tab-item>
              </v-tabs>
            </div>
            <div v-else>
              <v-skeleton-loader class="tw-w-96"
                type="list-item-two-line"
              ></v-skeleton-loader>
              <v-skeleton-loader
               class="tw-w-96"
                type="list-item-two-line"
              ></v-skeleton-loader>
              <v-skeleton-loader
               class="tw-w-162"
                type="list-item-two-line"
              ></v-skeleton-loader>
            </div>

          </v-card-text>
        </v-card>

        <v-dialog v-model="showDeleteDialog" max-width="400">
          <v-card>
            <v-card-title>ยืนยันการลบคำสั่ง</v-card-title>
            <v-card-text>
              คุณต้องการลบคำสั่ง "{{ promptToDelete?.promptName }}" ใช่หรือไม่?
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn text @click="closeDeleteDialog">ยกเลิก</v-btn>
              <v-btn
                color="error"
                :loading="deletingPrompt"
                @click="deletePrompt"
              >
                ลบ
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <v-dialog v-model="showEditTypeDialog" max-width="400">
          <v-card>
            <v-card-title>แก้ไขประเภทคำสั่ง</v-card-title>
            <v-card-text>
              <v-select
                v-model="editingPromptType"
                :items="promptTypes"
                item-text="text"
                item-value="value"
                label="ประเภทคำสั่ง"
                outlined
                dense
                return-object
              >
                <template v-slot:selection="{ item }">
                  <div class="tw-flex tw-items-center">
                    <v-icon left :color="item.color">{{ item.icon }}</v-icon>
                    {{ item.text }}
                  </div>
                </template>
                <template v-slot:item="{ item }">
                  <div class="tw-flex tw-items-center">
                    <v-icon left :color="item.color">{{ item.icon }}</v-icon>
                    {{ item.text }}
                  </div>
                </template>
              </v-select>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn text @click="closeEditTypeDialog">ยกเลิก</v-btn>
              <v-btn
                color="primary"
                @click="savePromptType"
              >
                บันทึก
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <v-dialog v-model="showNewPromptDialog" max-width="500px">
          <v-card>
            <v-card-title>เพิ่มคำสั่งใหม่</v-card-title>
            <v-card-text>
              <v-text-field
                v-model="newPrompt.name"
                label="ชื่อคำสั่ง"
                outlined
                dense
                class="tw-mb-4"
              ></v-text-field>
              
              <v-select
                v-model="newPrompt.type"
                :items="promptTypes"
                item-text="text"
                item-value="value"
                label="ประเภทคำสั่ง"
                outlined
                dense
                return-object
              >
                <template v-slot:selection="{ item }">
                  <div class="tw-flex tw-items-center">
                    <v-icon left :color="item.color">{{ item.icon }}</v-icon>
                    {{ item.text }}
                  </div>
                </template>
                <template v-slot:item="{ item }">
                  <div class="tw-flex tw-items-center">
                    <v-icon left :color="item.color">{{ item.icon }}</v-icon>
                    {{ item.text }}
                  </div>
                </template>
              </v-select>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="error" text @click="closeNewPromptDialog"
                >ยกเลิก</v-btn
              >
              <v-btn
                color="primary"
                :loading="savingPrompt"
                :disabled="!newPrompt.name || savingPrompt"
                @click="addNewPrompt"
              >
                เพิ่ม
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-container>
    </v-main>
  </Admin>
</template>

<script>
import Admin from "../../components/admin/admin-main-template";
import { mapState } from "vuex";
import _isEqual from "lodash/isEqual";
import _ from "lodash";
import fc from "@/firebaseConfig";

export default {
  name: "AI",
  components: { Admin },

  data: () => ({
    savingPrompt: false,
    savingChanges: false,
    snackbar: {
      show: false,
      message: "",
      color: "success",
    },
    items: [
      {
        text: "ตั้งค่า",
        disabled: false,
        to: { name: "Company.Settings" },
      },
      {
        text: "คำสั่ง Prompt",
        disabled: true,
      },
    ],
    currentTabIndex: 0,
    showKey: false,
    vendorSearch: "",
    isTestingConnection: false,
    isSavingApiKey: false,
    showNewPromptDialog: false,
    showDeleteDialog: false,
    showEditTypeDialog: false,
    promptToDelete: null,
    promptToEdit: null,
    editingPromptType: null,
    deletingPrompt: false,
    isActivatingPrompt: false,
    activatingPromptId: null,
    newPrompt: {
      name: "",
      text: "",
      type: null,
      selectedVendors: [],
    },
    hasUnsavedChanges: false,
    originalPrompts: null,
    apiKey: "",
    isInitializing: true,
    selectedPromptTypeFilter: "all",
    promptTypes: [
      { text: "Invoice", value: 1, icon: "mdi-file-document-outline", color: "indigo" },
      { text: "Credit Advice", value: 2, icon: "mdi-credit-card-outline", color: "black" }
    ]
  }),

  computed: {
    ...mapState({
      selectedCompany: (state) => state.companies.selectedCompany,
      selectedCompanyApVendors: (state) =>
        state.companies.selectedCompanyApVendors,
      prompts: (state) => state.companies.selectedCompanyApPrompts,
    }),
    currentPromptId() {
      return this.filteredPrompts[this.currentTabIndex]?.promptId || "";
    },
    filteredVendors() {
      return this.selectedCompanyApVendors.filter(
        (vendor) =>
          (vendor.venName || "")
            .toLowerCase()
            .includes((this.vendorSearch || "").toLowerCase()) && vendor.promptId === this.currentPromptId
      );
    },
    filteredPrompts() {
      if (this.selectedPromptTypeFilter === "all") return this.prompts;
      return this.prompts.filter(prompt => prompt.promptType === Number(this.selectedPromptTypeFilter));
    },
  },

  watch: {
    selectedCompany: {
      handler(newVal) {
        if (newVal) {
          this.apiKey = newVal.openAiKey || "";
        }
      },
      immediate: true,
    },
    prompts: {
      handler(newVal) {
        if (this.originalPrompts && !_isEqual(newVal, this.originalPrompts)) {
          this.hasUnsavedChanges = true;
        }
      },
      deep: true,
    },
  },

  mounted() {
    this.init();
  },

  methods: {
    getPromptTypeClass(type) {
      if (type === 1) return "prompt-type-invoice";
      if (type === 2) return "prompt-type-credit";
      return "";
    },
    
    getPromptTypeIcon(type) {
      if (type === 1) return "mdi-file-document-outline";
      if (type === 2) return "mdi-credit-card-outline";
      return "mdi-help-circle-outline";
    },
    
    getPromptTypeColor(type) {
      if (type === 1) return "indigo";
      if (type === 2) return "black";
      return "grey";
    },
    
    filterPromptsByType() {
      this.currentTabIndex = 0;
    },
    
    copyPromptId(promptId) {
      navigator.clipboard.writeText(promptId);
      this.notify("success", "คัดลอก ID คำสั่งสำเร็จ");
    },
    
    openEditTypeDialog(prompt) {
      this.promptToEdit = prompt;
      this.editingPromptType = this.promptTypes.find(t => t.value === prompt.promptType) || this.promptTypes[0];
      this.showEditTypeDialog = true;
    },
    
    closeEditTypeDialog() {
      this.promptToEdit = null;
      this.editingPromptType = null;
      this.showEditTypeDialog = false;
    },
    
    async savePromptType() {
      if (this.promptToEdit && this.editingPromptType) {
        this.updatePromptType(this.promptToEdit.promptId, this.editingPromptType);
        this.closeEditTypeDialog();
        
        // If the changes resulted in an inconsistent state, fix it immediately
        await this.ensureActivePromptsPerType();
        
        this.notify("success", "อัปเดตประเภทคำสั่งสำเร็จ");
      }
    },
    
    updatePromptType(promptId, value) {
      // Get the current prompt
      const currentPrompt = this.prompts.find(p => p.promptId === promptId);
      
      // Check if we're changing the type
      if (currentPrompt && currentPrompt.promptType !== Number(value.value)) {
   
        const newType = value.value;
        const oldType = currentPrompt.promptType;
        
        // Start with a copy of the prompts
        let updatedPrompts = [...this.prompts];
        
        // Check if the prompt we're updating is active
        const isCurrentPromptActive = currentPrompt.isActive;
        
        // Check if there's already an active prompt of the new type
        const existingActivePrompt = this.prompts.find(p => 
          p.promptType === newType && 
          p.isActive && 
          p.promptId !== promptId
        );
        
        // First pass: Update the type of the current prompt
        updatedPrompts = updatedPrompts.map(p => {
          if (p.promptId === promptId) {
            // If changing to a type that already has an active prompt,
            // this prompt should not be active
            const shouldBeActive = isCurrentPromptActive && !existingActivePrompt;
            return { 
              ...p, 
              promptType: newType,
              isActive: shouldBeActive 
            };
          }
          return p;
        });
        
        // If the current prompt was active, and there's no active prompt of the old type left,
        // activate the first prompt of the old type if one exists
        if (isCurrentPromptActive) {
          const oldTypePromptsRemaining = updatedPrompts.filter(p => 
            p.promptType === oldType && p.promptId !== promptId
          );
          
          if (oldTypePromptsRemaining.length > 0 && 
              !oldTypePromptsRemaining.some(p => p.isActive)) {
            // Activate the first prompt of the old type
            const firstOldTypePromptId = oldTypePromptsRemaining[0].promptId;
            updatedPrompts = updatedPrompts.map(p => {
              if (p.promptId === firstOldTypePromptId) {
                return { ...p, isActive: true };
              }
              return p;
            });
          }
        }
        
        this.$store.commit(
          "companies/setSelectedCompanyApPrompts",
          updatedPrompts
        );
        this.handlePromptChange();
        
        // Notify the user about the active status change if needed
        if (isCurrentPromptActive && existingActivePrompt) {
          this.notify(
            "info", 
            `ประเภทคำสั่งถูกเปลี่ยน และ "${existingActivePrompt.promptName}" ยังคงเป็นคำสั่งที่ใช้งานสำหรับประเภทนี้`
          );
        }
      } else {
        // If the type isn't changing, just update as normal
        const updatedPrompts = this.prompts.map((p) => {
          if (p.promptId === promptId) {
            return { ...p, promptType: value.value };
          }
          return p;
        });
        this.$store.commit(
          "companies/setSelectedCompanyApPrompts",
          updatedPrompts
        );
        this.handlePromptChange();
      }
    },
    
    async deletePrompt() {
      try {
        this.deletingPrompt = true;
        await this.$store.dispatch("companies/deletePrompt", {
          companyId: this.$route.params.companyId,
          promptId: this.promptToDelete.promptId,
        });
        this.notify("success", "ลบคำสั่งสำเร็จ");
        this.closeDeleteDialog();
      } catch (error) {
        this.notify("error", "เกิดข้อผิดพลาดในการลบคำสั่ง");
      } finally {
        this.deletingPrompt = false;
      }
    },
    
    notify(type, message) {
      this.snackbar = {
        show: true,
        message,
        color: type,
      };
    },
    
    openDeleteDialog(prompt) {
      this.promptToDelete = prompt;
      this.showDeleteDialog = true;
    },

    closeDeleteDialog() {
      this.promptToDelete = null;
      this.showDeleteDialog = false;
    },

    async init() {
      this.isInitializing = true
      const companyId = this.$route.params.companyId;
      if (companyId) {
        try {
          await Promise.all([
            this.$store.dispatch("user/getRoleUser", { companyId }),
            this.$store.dispatch("companies/fetchPrompts", { companyId }),
            this.fetchVendors(companyId),
          ]);
          
          // Ensure we have one active prompt per type if prompts exist for that type
          await this.ensureActivePromptsPerType();
          
          this.originalPrompts = _.cloneDeep(this.prompts);
        } catch (error) {
          this.notify("error", "เกิดข้อผิดพลาดในการดึงข้อมูล");
        }
      }
      this.isInitializing = false
    },
    
    async ensureActivePromptsPerType() {
      // Group prompts by type
      const promptsByType = _.groupBy(this.prompts, 'promptType');
      
      // Check each type to ensure there's exactly one active prompt
      let updatedPrompts = [...this.prompts];
      let hasChanges = false;
      
      for (const [type, prompts] of Object.entries(promptsByType)) {
        if (prompts.length > 0) {
          // Count active prompts for this type
          const activePrompts = prompts.filter(p => p.isActive);
          
          if (activePrompts.length === 0) {
            // No active prompts for this type, activate the first one
            const firstPromptId = prompts[0].promptId;
            updatedPrompts = updatedPrompts.map(p => {
              if (p.promptId === firstPromptId) {
                hasChanges = true;
                return { ...p, isActive: true };
              }
              return p;
            });
          } else if (activePrompts.length > 1) {
            // Multiple active prompts, keep only the first one active
            const keepActiveId = activePrompts[0].promptId;
            updatedPrompts = updatedPrompts.map(p => {
              if (p.promptType === type && p.isActive && p.promptId !== keepActiveId) {
                hasChanges = true;
                return { ...p, isActive: false };
              }
              return p;
            });
          }
        }
      }
      
      if (hasChanges) {
        // Update store with corrected active states
        this.$store.commit(
          "companies/setSelectedCompanyApPrompts",
          updatedPrompts
        );
        
        // Save changes to backend
        await this.$store.dispatch("companies/updatePrompts", {
          companyId: this.$route.params.companyId,
          prompts: updatedPrompts,
        });
      }
    },

    async fetchVendors(companyId) {
      const vendorsRef = await fc.companies
        .doc(companyId)
        .collection("PurchaseInvoiceVendors")
        .get();

      const vendors = vendorsRef.docs.map((doc) => ({
        vendorId: doc.id,
        ...doc.data(),
      }));

      this.$store.commit("companies/setSelectedCompanyApVendors", vendors);
    },
    
    updatePromptText(promptId, value) {
      const updatedPrompts = this.prompts.map((p) => {
        if (p.promptId === promptId) {
          return { ...p, promptText: value };
        }
        return p;
      });
      this.$store.commit(
        "companies/setSelectedCompanyApPrompts",
        updatedPrompts
      );
      this.handlePromptChange();
    },

    toggleShowKey() {
      this.showKey = !this.showKey;
    },

    openNewPromptDialog() {
      this.newPrompt = {
        name: "",
        text: "",
        type: this.promptTypes[0],
        selectedVendors: [],
      };
      this.showNewPromptDialog = true;
    },

    closeNewPromptDialog() {
      this.showNewPromptDialog = false;
      this.newPrompt = {
        name: "",
        text: "",
        type: null,
        selectedVendors: [],
      };
    },

    async setActivePrompt(prompt) {
      try {
        this.isActivatingPrompt = true;
        this.activatingPromptId = prompt.promptId;
        
        // Check if there's already an active prompt of this type
        const currentActivePrompt = this.prompts.find(p => 
          p.promptType === prompt.promptType && p.isActive
        );
        
        // Deactivate the current active prompt of this type
        let updatedPrompts = [...this.prompts];
        if (currentActivePrompt) {
          updatedPrompts = updatedPrompts.map(p => {
            if (p.promptId === currentActivePrompt.promptId) {
              return { ...p, isActive: false };
            }
            return p;
          });
        }
        
        // Activate the new prompt
        updatedPrompts = updatedPrompts.map(p => {
          if (p.promptId === prompt.promptId) {
            return { ...p, isActive: true };
          }
          return p;
        });
        
        // Update store
        this.$store.commit(
          "companies/setSelectedCompanyApPrompts",
          updatedPrompts
        );
        
        // Save changes
        await this.$store.dispatch("companies/updatePrompts", {
          companyId: this.$route.params.companyId,
          prompts: updatedPrompts,
        });
        
        this.originalPrompts = _.cloneDeep(updatedPrompts);
        this.hasUnsavedChanges = false;
        this.notify("success", "ตั้งค่าคำสั่งเป็นใช้งานสำเร็จ");
      } catch (error) {
        this.notify("error", "เกิดข้อผิดพลาดในการตั้งค่าคำสั่ง");
      } finally {
        this.isActivatingPrompt = false;
        this.activatingPromptId = null;
      }
    },
    
    async addNewPrompt() {
      try {
        this.savingPrompt = true;
        
        // Check if we should automatically set this prompt as active
        // This happens if there are no other prompts of this type
        const existingPromptsOfType = this.prompts.filter(p => 
          p.promptType === this.newPrompt.type.value
        );
        const shouldSetActive = existingPromptsOfType.length === 0;
        
        const newPrompt = {
          promptName: this.newPrompt.name,
          promptText: "",
          promptType: this.newPrompt.type.value,
          isActive: shouldSetActive,
          selectedVendors: [],
        };

        await this.$store.dispatch("companies/addPrompt", {
          companyId: this.$route.params.companyId,
          prompt: newPrompt,
        });

        this.closeNewPromptDialog();
        this.notify("success", shouldSetActive ? 
          "เพิ่มคำสั่งสำเร็จและตั้งเป็นใช้งาน" : 
          "เพิ่มคำสั่งสำเร็จ"
        );
      } catch (error) {
        this.notify("error", "เกิดข้อผิดพลาดในการเพิ่มคำสั่ง");
      } finally {
        this.savingPrompt = false;
      }
    },

    handlePromptChange() {
      this.hasUnsาedChanges = true;
    },

    handleVendorSelection() {
      this.hasUnsavedChanges = true;
    },

    async savePromptChanges() {
      try {
        this.savingChanges = true;
        
        // Ensure we have one active prompt per type before saving
        await this.ensureActivePromptsPerType();
        
        await this.$store.dispatch("companies/updatePrompts", {
          companyId: this.$route.params.companyId,
          prompts: this.prompts,
        });

        this.hasUnsavedChanges = false;
        this.originalPrompts = _.cloneDeep(this.prompts);
        this.notify("success", "บันทึกการเปลี่ยนแปลงสำเร็จ");
      } catch (error) {
        this.notify("error", "เกิดข้อผิดพลาดในการบันทึกการเปลี่ยนแปลง");
      } finally {
        this.savingChanges = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.v-tab {
  text-transform: none;
  padding-right: 8px !important;

  .v-btn {
    margin-left: 8px;
  }
}

.prompt-type-invoice {
  border-left: 3px solid #3f51b5;
}

.prompt-type-credit {
  border-left: 3px solid #000000;
}

.active-prompt {
  background-color: #f5f9ff !important;
  font-weight: 500;
}

.active-badge {
  position: absolute;
  top: -4px;
  left: -10px;
}
</style>